import { CreditCardType, PaymentType } from '@/models/app-config/behavior/types'
import { CarrierProvider } from '@/models/purhase/carrier_provider'
import { CvsType } from '@/models/purhase/cvs_type'
import { anyOf, join, notNil, Parser, requireKeys } from '@/util/parse-object'
import { Either, left, right } from 'fp-ts/lib/Either'

/*
 * 注文データの内、決済方法に関する部分を変換するための型定義・関数を定義するための内部モジュール.
 * 内部モジュールなので、各種クライアントコードはこのモジュールを直接参照しないこと.
 * 特に @internal タグが付与されている型定義には注意.
 *
 * クライアントコード側でアクセスする必要がある型定義・関数は
 * client/spa/src/store/modules/orderHistory/types.ts で別途exportすること.
 */

export type InnerPaymentMethod =
  InnerPaymentMethodWithoutAdditionalData |
  InnerCreditCardPaymentMethod |
  InnerCvsPaymentMethod |
  InnerCarrierPaymentMethod |
  InnerNonePaymentMethod

/**
 * 固有の追加データを特に持たない支払種別
 */
export type PaymentTypeWithoutAdditionalData = Exclude<PaymentType, CreditCardType | 'cvs-number' | 'carrier'>
/**
 * 固有の追加データを特に持たない決済方法
 * @internal
 */
export interface InnerPaymentMethodWithoutAdditionalData {
  kind: PaymentTypeWithoutAdditionalData
}
/**
 * 決済無し
 * @private
 */
export interface InnerNonePaymentMethod {
  kind: 'none'
}

/**
 * 固有の追加データを特に持つ決済種別
 */
export type PaymentTypeWithAdditionalData = Exclude<PaymentType, PaymentTypeWithoutAdditionalData>
export type PaymentMethodWithAdditionalData<
  Kind extends PaymentTypeWithAdditionalData,
  Data extends object
> = Data & {
  kind: Kind
}
/**
 * クレジットカード利用時
 * @internal
 */
export type InnerCreditCardPaymentMethod =
  PaymentMethodWithAdditionalData<CreditCardType, { token: string }>
/**
 * クレジットカード利用時
 * @internal
 */
export type InnerCvsPaymentMethod =
  PaymentMethodWithAdditionalData<'cvs-number', { cvs_type: CvsType }>
/**
 * キャリア決済利用時型
 * @internal
 */
export type InnerCarrierPaymentMethod =
  PaymentMethodWithAdditionalData<'carrier', { carrier_provider: CarrierProvider }>

export function convertPaymentMethod(unparsed: object): Either<Error[], InnerPaymentMethod> {
  interface DataWithKind { kind: InnerPaymentMethod['kind'] }

  return join<DataWithKind>(
    requireKeys(['kind']),
    notNil(['kind']),
    anyOf('kind', ['creditcard', 'creditcard3d', 'cvs-number', 'carrier', 'cash-on-delivery', 'atone', 'none', 'np-postpay', 'paidy', 'webmoney'])
  )(unparsed).fold(
    error => left(error),
    (data: DataWithKind) => {
      const parser: Parser<InnerPaymentMethod, DataWithKind> = ((): Parser<InnerPaymentMethod> => {
        const kind = data.kind
        switch (kind) {
          case 'creditcard':
          case 'creditcard3d':
            return join<InnerCreditCardPaymentMethod>(
              requireKeys(['token']),
              notNil(['token'])
            )
          case 'cvs-number':
            return join<InnerCvsPaymentMethod>(
              requireKeys(['cvs_type']),
              anyOf('cvs_type', ['seven-eleven', 'lawson', 'familymart', 'seicomart', 'ministop'])
            )
          case 'carrier':
            return join<InnerCarrierPaymentMethod>(
              requireKeys(['carrier_provider']),
              anyOf('carrier_provider', ['docomo', 'au', 'softbank'])
            )
          default:
            return () => right<Error[], InnerPaymentMethod>({ kind })
        }
      })()

      return parser(data)
    }
  )
}
